/**
 * Apply Kanban WebSocket domain events onto `KanbanFullBoard` state (spec: temporary/kanban/KANBAN_WEBSOCKET.md).
 */

import type {
  KanbanBoard,
  KanbanBoardSetting,
  KanbanCadence,
  KanbanCardSummary,
  KanbanFullBoard,
  KanbanLabel,
  KanbanList,
  KanbanSwimlane,
} from '../types/kanban';
import type { KanbanWsEnvelope } from '../types/kanbanRealtime';
import { coerceKanbanEntityId } from './kanbanEntityIds';

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

function emptyCardSummary(boardId: string, id: string): KanbanCardSummary {
  return {
    id,
    list_id: '',
    board_id: boardId,
    swimlane_id: null,
    title: '',
    description: null,
    position: 0,
    priority: 5,
    card_type: 'task',
    class_of_service: 'standard',
    due_date: null,
    start_date: null,
    completed_at: null,
    is_blocked: 0,
    blocked_reason: null,
    is_archived: 0,
    cover_color: null,
    cover_image_url: null,
    list_entered_at: Date.now(),
    metadata: null,
    created_at: Date.now(),
    created_by: '',
    updated_at: null,
    updated_by: null,
    labels: [],
    assignees: [],
    checklist_progress: { total: 0, checked: 0 },
    comment_count: 0,
    attachment_count: 0,
    watcher_count: 0,
    link_count: 0,
  };
}

function mergeCardSummary(existing: KanbanCardSummary, incoming: Record<string, unknown>): KanbanCardSummary {
  const next = { ...existing } as Record<string, unknown>;
  for (const [k, v] of Object.entries(incoming)) {
    if (v === undefined) continue;
    if (k === 'labels' && Array.isArray(v)) {
      if (v.length === 0 && (existing.labels?.length ?? 0) > 0) continue;
      next.labels = v as KanbanCardSummary['labels'];
    } else if (k === 'assignees' && Array.isArray(v)) {
      if (v.length === 0 && (existing.assignees?.length ?? 0) > 0) continue;
      next.assignees = v as KanbanCardSummary['assignees'];
    } else if (k === 'checklist_progress' && v && typeof v === 'object') {
      const cp = v as Record<string, unknown>;
      const total = typeof cp.total === 'number' ? cp.total : 0;
      const checked = typeof cp.checked === 'number' ? cp.checked : 0;
      if (total === 0 && checked === 0 && existing.checklist_progress.total > 0) continue;
      next.checklist_progress = { total, checked };
    } else if (
      (k === 'comment_count' || k === 'attachment_count' || k === 'watcher_count' || k === 'link_count') &&
      typeof v === 'number' &&
      v === 0
    ) {
      const prev = existing[k as keyof KanbanCardSummary];
      if (typeof prev === 'number' && prev > 0) continue;
      next[k] = v;
    } else {
      next[k] = v;
    }
  }
  return next as unknown as KanbanCardSummary;
}

function coerceCardSummary(boardId: string, raw: unknown): KanbanCardSummary | null {
  const o = asRecord(raw);
  if (!o || typeof o.id !== 'string') return null;
  const base = emptyCardSummary(boardId, o.id);
  return mergeCardSummary(base, o);
}

function coerceList(raw: unknown): KanbanList | null {
  const o = asRecord(raw);
  if (!o || typeof o.id !== 'string' || typeof o.board_id !== 'string') return null;
  return o as unknown as KanbanList;
}

function coerceSwimlane(raw: unknown): KanbanSwimlane | null {
  const o = asRecord(raw);
  if (!o || typeof o.id !== 'string') return null;
  return o as unknown as KanbanSwimlane;
}

function coerceLabel(raw: unknown): KanbanLabel | null {
  const o = asRecord(raw);
  if (!o || typeof o.id !== 'string') return null;
  return o as unknown as KanbanLabel;
}

function coerceBoard(raw: unknown): KanbanBoard | null {
  const o = asRecord(raw);
  if (!o) return null;
  const id = coerceKanbanEntityId(o.id);
  if (id == null) return null;
  return { ...o, id } as unknown as KanbanBoard;
}

/** Prefer existing non-empty string / positive timestamp when WS payload is partial. */
function mergeBoardSnapshot(prev: KanbanBoard, incoming: KanbanBoard): KanbanBoard {
  return {
    ...prev,
    ...incoming,
    permissions: prev.permissions,
    title: incoming.title.trim() !== '' ? incoming.title : prev.title,
    team: incoming.team.trim() !== '' ? incoming.team : prev.team,
    created_by: incoming.created_by.trim() !== '' ? incoming.created_by : prev.created_by,
    description:
      incoming.description != null && String(incoming.description).trim() !== ''
        ? incoming.description
        : prev.description,
    background_color:
      incoming.background_color != null && String(incoming.background_color).trim() !== ''
        ? incoming.background_color
        : prev.background_color,
    created_at: incoming.created_at > 0 ? incoming.created_at : prev.created_at,
    is_default: typeof incoming.is_default === 'number' ? incoming.is_default : prev.is_default,
    is_archived: typeof incoming.is_archived === 'number' ? incoming.is_archived : prev.is_archived,
    updated_at: incoming.updated_at ?? prev.updated_at,
    updated_by: incoming.updated_by ?? prev.updated_by,
  };
}

function coerceSetting(raw: unknown): KanbanBoardSetting | null {
  const o = asRecord(raw);
  if (!o || typeof o.setting_key !== 'string') return null;
  return o as unknown as KanbanBoardSetting;
}

function coerceCadence(raw: unknown): KanbanCadence | null {
  const o = asRecord(raw);
  if (!o || typeof o.id !== 'string') return null;
  return o as unknown as KanbanCadence;
}

export interface MergeKanbanRealtimeResult {
  next: KanbanFullBoard;
  /** Caller should refetch full board (gap, unknown type, or destructive ambiguity). */
  healFull: boolean;
  /** Open card detail should refetch from API. */
  refetchCardDetailId: string | null;
}

const DOMAIN_TYPES_HEAL = new Set([
  'card.checklists_changed',
  'card.comments_changed',
  'card.attachments_changed',
  'card.links_changed',
]);

/**
 * Apply one WS envelope. When `healFull` is true, caller should replace state via GET /full.
 */
export function mergeKanbanBoardEvent(prev: KanbanFullBoard, env: KanbanWsEnvelope): MergeKanbanRealtimeResult {
  const boardId = prev.board.id;
  const p = env.payload ?? {};
  let refetchCardDetailId: string | null = null;

  const heal = (): MergeKanbanRealtimeResult => ({ next: prev, healFull: true, refetchCardDetailId: null });

  if (env.board_id != null && env.board_id !== boardId) {
    return { next: prev, healFull: false, refetchCardDetailId: null };
  }

  const type = env.type;

  if (type.startsWith('system.')) {
    return { next: prev, healFull: false, refetchCardDetailId: null };
  }

  if (type === 'presence.update') {
    return { next: prev, healFull: false, refetchCardDetailId: null };
  }

  if (DOMAIN_TYPES_HEAL.has(type)) {
    return heal();
  }

  if (type === 'card.assignees_changed' || type === 'card.labels_changed') {
    const cardId = typeof p.card_id === 'string' ? p.card_id : asRecord(p.card)?.id;
    const cardPayload = coerceCardSummary(boardId, p.card);
    if (cardPayload && typeof cardId === 'string' && cardPayload.id === cardId) {
      const cards = prev.cards.map((c) => (c.id === cardId ? mergeCardSummary(c, cardPayload as unknown as Record<string, unknown>) : c));
      return { next: { ...prev, cards }, healFull: false, refetchCardDetailId: null };
    }
    return heal();
  }

  if (p.refetch_card_detail === true) {
    const cid = asRecord(p.card)?.id;
    refetchCardDetailId = typeof cid === 'string' ? cid : typeof p.card_id === 'string' ? p.card_id : null;
  }

  const upsertCard = (raw: unknown): KanbanFullBoard | null => {
    const c = coerceCardSummary(boardId, raw);
    if (!c) return null;
    const idx = prev.cards.findIndex((x) => x.id === c.id);
    const cards =
      idx === -1 ? [...prev.cards, c] : prev.cards.map((x, i) => (i === idx ? mergeCardSummary(x, c as unknown as Record<string, unknown>) : x));
    return { ...prev, cards };
  };

  const removeCard = (cardId: string): KanbanFullBoard => ({
    ...prev,
    cards: prev.cards.filter((c) => c.id !== cardId),
  });

  switch (type) {
    case 'card.created':
    case 'card.updated':
    case 'card.moved':
    case 'card.archived':
    case 'card.unarchived': {
      const raw = p.card ?? (type === 'card.updated' ? { ...asRecord(p.patch), ...asRecord(p.card) } : p.card);
      const nextBoard = upsertCard(raw);
      return nextBoard
        ? { next: nextBoard, healFull: false, refetchCardDetailId }
        : heal();
    }

    case 'card.deleted': {
      const id = typeof p.card_id === 'string' ? p.card_id : null;
      if (!id) return heal();
      return { next: removeCard(id), healFull: false, refetchCardDetailId: null };
    }

    case 'cards.batch_moved': {
      const moves = Array.isArray(p.moves) ? p.moves : [];
      let cards = [...prev.cards];
      for (const m of moves) {
        const o = asRecord(m);
        if (!o) continue;
        const cardId = typeof o.card_id === 'string' ? o.card_id : typeof o.id === 'string' ? o.id : null;
        if (!cardId) continue;
        const full = coerceCardSummary(boardId, o.card ?? o);
        const patch: Record<string, unknown> = full
          ? (full as unknown as Record<string, unknown>)
          : {};
        if (typeof o.list_id === 'string') patch.list_id = o.list_id;
        if ('swimlane_id' in o) patch.swimlane_id = o.swimlane_id;
        if (typeof o.position === 'number') patch.position = o.position;
        const idx = cards.findIndex((c) => c.id === cardId);
        if (idx === -1) return heal();
        cards[idx] = mergeCardSummary(cards[idx], patch);
      }
      return { next: { ...prev, cards }, healFull: false, refetchCardDetailId: null };
    }

    case 'list.created':
    case 'list.updated':
    case 'list.reordered':
    case 'list.archived':
    case 'list.unarchived': {
      const L = coerceList(p.list);
      if (!L) return heal();
      const idx = prev.lists.findIndex((l) => l.id === L.id);
      const lists = idx === -1 ? [...prev.lists, L] : prev.lists.map((l, i) => (i === idx ? { ...l, ...L } : l));
      return { next: { ...prev, lists }, healFull: false, refetchCardDetailId: null };
    }

    case 'list.deleted': {
      const id = asRecord(p.list)?.id ?? p.list;
      const listId = typeof id === 'string' ? id : null;
      if (!listId) return heal();
      return {
        next: {
          ...prev,
          lists: prev.lists.filter((l) => l.id !== listId),
          cards: prev.cards.filter((c) => c.list_id !== listId),
        },
        healFull: false,
        refetchCardDetailId: null,
      };
    }

    case 'swimlane.created':
    case 'swimlane.updated':
    case 'swimlane.reordered':
    case 'swimlane.collapsed': {
      const S = coerceSwimlane(p.swimlane);
      if (!S) return heal();
      const idx = prev.swimlanes.findIndex((s) => s.id === S.id);
      const swimlanes = idx === -1 ? [...prev.swimlanes, S] : prev.swimlanes.map((s, i) => (i === idx ? { ...s, ...S } : s));
      return { next: { ...prev, swimlanes }, healFull: false, refetchCardDetailId: null };
    }

    case 'swimlane.deleted': {
      const id = asRecord(p.swimlane)?.id;
      const sid = typeof id === 'string' ? id : null;
      if (!sid) return heal();
      return {
        next: {
          ...prev,
          swimlanes: prev.swimlanes.filter((s) => s.id !== sid),
          cards: prev.cards.map((c) => (c.swimlane_id === sid ? { ...c, swimlane_id: null } : c)),
        },
        healFull: false,
        refetchCardDetailId: null,
      };
    }

    case 'label.created':
    case 'label.updated': {
      const lb = coerceLabel(p.label);
      if (!lb) return heal();
      const idx = prev.labels.findIndex((l) => l.id === lb.id);
      const labels = idx === -1 ? [...prev.labels, lb] : prev.labels.map((l, i) => (i === idx ? { ...l, ...lb } : l));
      return { next: { ...prev, labels }, healFull: false, refetchCardDetailId: null };
    }

    case 'label.deleted': {
      const id = asRecord(p.label)?.id;
      const lid = typeof id === 'string' ? id : null;
      if (!lid) return heal();
      return {
        next: {
          ...prev,
          labels: prev.labels.filter((l) => l.id !== lid),
          cards: prev.cards.map((c) => ({
            ...c,
            labels: c.labels.filter((x) => x.id !== lid),
          })),
        },
        healFull: false,
        refetchCardDetailId: null,
      };
    }

    case 'board.updated':
    case 'board.archived':
    case 'board.unarchived': {
      const B = coerceBoard(p.board);
      if (!B || B.id !== boardId) return heal();
      return { next: { ...prev, board: { ...prev.board, ...B, permissions: prev.board.permissions } }, healFull: false, refetchCardDetailId: null };
    }

    case 'board.deleted':
      return heal();

    case 'board.settings_changed': {
      const row = coerceSetting(p.setting);
      if (!row) return heal();
      const others = prev.settings.filter((s) => s.setting_key !== row.setting_key);
      return { next: { ...prev, settings: [...others, row] }, healFull: false, refetchCardDetailId: null };
    }

    case 'cadence.created':
    case 'cadence.updated': {
      const cad = coerceCadence(p.cadence);
      if (!cad || cad.board_id !== boardId) return { next: prev, healFull: false, refetchCardDetailId: null };
      return { next: prev, healFull: false, refetchCardDetailId: null };
    }

    case 'cadence.deleted':
      return { next: prev, healFull: false, refetchCardDetailId: null };

    default:
      return { next: prev, healFull: false, refetchCardDetailId: null };
  }
}

/** Team room: workspace index board list (same envelope types, team `seq`). */
export function mergeTeamBoardsList(
  boards: KanbanBoard[],
  teamSlug: string,
  env: KanbanWsEnvelope,
): { next: KanbanBoard[]; heal: boolean } {
  const p = env.payload ?? {};
  const B = coerceBoard(p.board);
  const type = env.type;

  if (type === 'board.created' && B && B.team === teamSlug) {
    const exists = boards.some((b) => b.id === B.id);
    if (exists) {
      return {
        next: boards.map((b) => (b.id === B.id ? mergeBoardSnapshot(b, B) : b)),
        heal: false,
      };
    }
    return { next: [B, ...boards], heal: false };
  }

  if ((type === 'board.updated' || type === 'board.archived' || type === 'board.unarchived') && B) {
    const idx = boards.findIndex((b) => b.id === B.id);
    if (idx === -1) return { next: boards, heal: true };
    return { next: boards.map((b, i) => (i === idx ? mergeBoardSnapshot(b, B) : b)), heal: false };
  }

  if (type === 'board.deleted') {
    const id = B?.id ?? (typeof p.board_id === 'string' ? p.board_id : null);
    if (!id) return { next: boards, heal: true };
    return { next: boards.filter((b) => b.id !== id), heal: false };
  }

  return { next: boards, heal: false };
}
